import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeepSpaceBackground from "../../../components/northstar/DeepSpaceBackground";
import { Button } from "../../../components/ui/button";
import { api } from "../../../lib/auth";

function StatusPill({ status }) {
  const map = {
    REQUESTED: { label: "Requested", cls: "bg-[#7ED493]/20 text-[#7ED493] border border-[#7ED493]/40" },
    WITHDRAWN: { label: "Withdrawn", cls: "bg-white/10 text-white/70 border border-white/20" },
  };
  const s = map[status] || { label: status, cls: "bg-white/10 text-white/70" };
  return (
    <span data-testid={`connection-status-${status}`}
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function MyConnections() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  async function load() {
    setError(null);
    try {
      // BTC3 Phase 7 — canonical Bearer transport via the shared
      // `api` axios instance.  Legacy cookie fallback removed.
      const { data } = await api.get("/northstar/connections");
      setRows(data.connections || []);
    } catch (e) {
      setError("We couldn't load your connections just now.");
    }
  }

  useEffect(() => { load(); }, []);

  async function withdraw(id) {
    setBusy(id);
    try {
      await api.post(`/northstar/connections/${id}/withdraw`);
      await load();
    } catch {
      setError("We couldn't withdraw that connection just now.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen text-white relative">
      <DeepSpaceBackground />
      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12" data-testid="my-connections">
        <div className="mb-8">
          <Link to="/northstar" className="text-white/60 hover:text-white text-sm"
                data-testid="my-connections-back">← Back to Northstar</Link>
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Your connections</h1>
        <p className="text-white/70 mb-8">
          Services you've asked BTC to help you connect with. You can withdraw
          any pending connection at any time.
        </p>

        {error && (
          <div data-testid="my-connections-error"
               className="mb-6 border border-white/20 bg-white/5 rounded-lg p-4">
            <p className="text-sm text-white/80 mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={load}
                    data-testid="my-connections-retry">Try again</Button>
          </div>
        )}

        {rows === null && !error && (
          <div data-testid="my-connections-loading" className="text-white/60">Loading…</div>
        )}

        {rows !== null && rows.length === 0 && !error && (
          <div data-testid="my-connections-empty"
               className="border border-white/10 rounded-lg p-8 text-white/70">
            You haven't asked to connect with any services yet. Open a Northstar
            area to explore what's available.
          </div>
        )}

        {rows && rows.length > 0 && (
          <ul className="space-y-4" data-testid="my-connections-list">
            {rows.map((r) => (
              <li key={r.id}
                  data-testid={`connection-row-${r.id}`}
                  className="border border-white/10 rounded-lg p-5 bg-white/[0.03]">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-lg font-medium truncate">
                        {r.entity_snapshot?.canonical_name || "BTC service"}
                      </h2>
                      <StatusPill status={r.status} />
                    </div>
                    {r.entity_snapshot?.public_description && (
                      <p className="text-sm text-white/70 mb-2">
                        {r.entity_snapshot.public_description}
                      </p>
                    )}
                    <p className="text-xs text-white/50">
                      Requested {new Date(r.created_at).toLocaleDateString()}
                      {r.withdrawn_at &&
                        ` · withdrawn ${new Date(r.withdrawn_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  {r.status === "REQUESTED" && (
                    <Button
                      variant="outline" size="sm"
                      disabled={busy === r.id}
                      onClick={() => withdraw(r.id)}
                      data-testid={`connection-withdraw-${r.id}`}
                    >{busy === r.id ? "Withdrawing…" : "Withdraw"}</Button>
                  )}
                </div>
                {r.entity_snapshot?.provenance && (
                  <p className="mt-3 text-xs text-white/40 italic">
                    {r.entity_snapshot.provenance}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
